import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { LoginContext } from '../../Contexts/LoginContext';
import { showToast } from '../../Components/utils/ShowToast';
import { formatCurrency, formatUsdToLocal } from '../../data/constants';
import Loader from '../../Components/utils/Loader';
import { FaWallet, FaArrowRight, FaCreditCard, FaHistory } from 'react-icons/fa';

export default function AccountCreditsPage() {
  const { user, currency, exchangeRate } = useContext(LoginContext);
  const [balance, setBalance] = useState(null);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [requesting, setRequesting] = useState(false);

  const fetchBalance = async () => {
    if (!user?._id) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/buyer/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBalance(res.data?.balance ?? 0);
      setPendingPayouts(res.data?.pendingPayouts ?? []);
    } catch (err) {
      setBalance(0);
      setPendingPayouts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalance();
  }, [user?._id]);

  const handleRequestPayout = async (e) => {
    e.preventDefault();
    const amount = parseFloat(payoutAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      showToast('Enter a valid amount', 'error');
      return;
    }
    if (amount > (balance ?? 0)) {
      showToast('Insufficient credits', 'error');
      return;
    }
    setRequesting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/buyer/request-refund-payout`,
        { amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Payout request submitted. Admin will process it.', 'success');
      setPayoutAmount('');
      fetchBalance();
    } catch (err) {
      showToast(err.response?.data?.message || 'Request failed', 'error');
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return (
      <div className="accountCreditsPage accountCreditsPageLoading">
        <Loader type="simple" />
      </div>
    );
  }

  const available = balance ?? 0;

  return (
    <div className="accountCreditsPage">
      <div className="accountCreditsHero">
        <div className="accountCreditsHeroIcon">
          <FaWallet size={28} />
        </div>
        <h1 className="accountCreditsTitle">Account credits</h1>
        <p className="accountCreditsSubtitle">
          Refunds from cancelled orders are added here. Use credits at checkout or request a payout to your payment method.
        </p>
      </div>

      <div className="accountCreditsCards">
        <section className="accountCreditsCard accountCreditsBalanceCard">
          <div className="accountCreditsCardHeader">
            <span className="accountCreditsCardIcon"><FaWallet /></span>
            <div>
              <h2 className="accountCreditsCardTitle">Available balance</h2>
              <p className="accountCreditsCardHint">Use at checkout or request a payout below</p>
            </div>
          </div>
          <div className="accountCreditsCardBody">
            <p className="accountCreditsBalanceAmount">
              {formatCurrency(available, currency || 'USD', exchangeRate ?? 1)}
            </p>
            <Link to="/checkoutPage" className="accountCreditsCtaBtn">
              Go to checkout <FaArrowRight size={14} />
            </Link>
          </div>
        </section>

        <section className="accountCreditsCard">
          <div className="accountCreditsCardHeader">
            <span className="accountCreditsCardIcon"><FaCreditCard /></span>
            <div>
              <h2 className="accountCreditsCardTitle">Request payout</h2>
              <p className="accountCreditsCardHint">Get your credits paid to your original payment method. Admin will process your request.</p>
            </div>
          </div>
          <div className="accountCreditsCardBody">
            <form onSubmit={handleRequestPayout} className="accountCreditsPayoutForm">
              <div className="accountCreditsFormGroup">
                <label className="accountCreditsLabel">Amount</label>
                <input
                  type="number"
                  min="0.01"
                  max={available}
                  step="0.01"
                  className="inputField accountCreditsInput"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  placeholder="0.00"
                />
                <span className="accountCreditsMaxHint">Max: {formatCurrency(available, currency || 'USD', exchangeRate ?? 1)}</span>
                {currency !== 'USD' && parseFloat(payoutAmount) > 0 && (
                  <span className="cpLocalPreview">{formatUsdToLocal(payoutAmount, currency, exchangeRate)}</span>
                )}
              </div>
              <button
                type="submit"
                className="modalButton submit accountCreditsSubmitBtn"
                disabled={requesting || available <= 0 || !payoutAmount || parseFloat(payoutAmount) <= 0}
              >
                {requesting ? <Loader type="miniPlus" /> : 'Request payout'}
              </button>
            </form>
          </div>
        </section>

        {pendingPayouts.length > 0 && (
          <section className="accountCreditsCard">
            <div className="accountCreditsCardHeader">
              <span className="accountCreditsCardIcon"><FaHistory /></span>
              <div>
                <h2 className="accountCreditsCardTitle">Pending payout requests</h2>
                <p className="accountCreditsCardHint">Requests are processed by admin</p>
              </div>
            </div>
            <div className="accountCreditsCardBody">
              <ul className="accountCreditsPendingList">
                {pendingPayouts.map((p) => (
                  <li key={p._id} className="accountCreditsPendingItem">
                    <span className="accountCreditsPendingAmount">{formatCurrency(p.amount, currency || 'USD', exchangeRate ?? 1)}</span>
                    <span className="accountCreditsPendingMeta">
                      Requested {new Date(p.createdAt).toLocaleDateString()} · Pending
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
